// src/pages/CampanhasAnalise.jsx
//
// Tela de LISTAGEM das campanhas analisadas.
// Consome: GET /analise/campaigns
// Navega para: /campanhas/:external_id/analise
//
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  RefreshCw,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  Search,
} from "lucide-react";
import { toast } from "react-toastify";
import analiseApi from "../services/analiseApiService";

// ============================================================================
// Helpers de formatação
// ============================================================================

const formatBRL = (v) => {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(v);
};

const formatRelativeTime = (ms) => {
  if (!ms) return "nunca";
  const diffMs = Date.now() - ms;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "agora há pouco";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `há ${diffD}d`;
  return new Date(ms).toLocaleDateString("pt-BR");
};

// ============================================================================
// Componente principal
// ============================================================================

export default function CampanhasAnalise() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analiseApi.get("/campaigns");
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Não foi possível carregar campanhas";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Filtro de busca
  const filtered = campaigns.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.campaign_name?.toLowerCase().includes(q) ||
      String(c.external_id).includes(q)
    );
  });

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Análise de Campanhas
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Acompanhe o desempenho das suas campanhas com dados cruzados
              do Google Ads e do tracker.
            </p>
          </div>

          <button
            onClick={loadCampaigns}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 shrink-0 text-sm disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>

        {/* Erro */}
        {error && !loading && (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm dark:bg-red-900/20 dark:text-red-400 dark:border-red-900">
            {error}
          </div>
        )}

        {/* Busca */}
        {!loading && campaigns.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou ID da campanha..."
              className="w-full pl-10 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
            />
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-12">
            <p className="text-center text-sm text-slate-500 dark:text-slate-400">
              Carregando campanhas...
            </p>
          </div>
        )}

        {/* Vazio */}
        {!loading && campaigns.length === 0 && !error && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 md:p-12 text-center">
            <BarChart3 className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
              Nenhuma campanha sincronizada ainda
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
              Sincronize uma campanha do Google Ads para começar a analisar
              seus dados aqui.
            </p>
          </div>
        )}

        {/* Sem resultados de busca */}
        {!loading && campaigns.length > 0 && filtered.length === 0 && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Nenhuma campanha encontrada para "{search}".
            </p>
          </div>
        )}

        {/* Lista */}
        {!loading && filtered.length > 0 && (
          <>
            {/* Mobile: Cards */}
            <div className="lg:hidden space-y-3">
              {filtered.map((c) => (
                <CampaignCardMobile
                  key={c.external_id}
                  campaign={c}
                  onOpen={() =>
                    navigate(`/campanhas/${c.external_id}/analise`)
                  }
                />
              ))}
            </div>

            {/* Desktop: Tabela */}
            <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
              <div className="w-full overflow-x-auto">
                <table className="w-full table-fixed text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium w-[35%]">
                        Campanha
                      </th>
                      <th className="text-left px-4 py-3 font-medium w-[12%]">
                        Status
                      </th>
                      <th className="text-right px-4 py-3 font-medium w-[14%]">
                        Custo 30d
                      </th>
                      <th className="text-right px-4 py-3 font-medium w-[14%]">
                        Faturamento 30d
                      </th>
                      <th className="text-right px-4 py-3 font-medium w-[10%]">
                        Resultado
                      </th>
                      <th className="text-left px-4 py-3 font-medium w-[15%]">
                        Última sincronização
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filtered.map((c) => (
                      <CampaignRowDesktop
                        key={c.external_id}
                        campaign={c}
                        onOpen={() =>
                          navigate(`/campanhas/${c.external_id}/analise`)
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Card mobile (uma campanha)
// ============================================================================

function CampaignCardMobile({ campaign: c, onOpen }) {
  const resultado = (c.faturamento_total_30d || 0) - (c.custo_total_30d || 0);

  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
            {c.campaign_name || "—"}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-mono">
            ID: {c.external_id}
          </p>
        </div>
        <StatusBadge status={c.campaign_status} />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Custo 30d</p>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-0.5">
            {formatBRL(c.custo_total_30d)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Faturamento 30d</p>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-0.5">
            {formatBRL(c.faturamento_total_30d)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Resultado</p>
          <p
            className={`text-sm font-semibold mt-0.5 ${
              resultado > 0
                ? "text-emerald-600 dark:text-emerald-400"
                : resultado < 0
                ? "text-red-600 dark:text-red-400"
                : "text-slate-900 dark:text-slate-100"
            }`}
          >
            {formatBRL(resultado)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Última sync</p>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-0.5">
            {formatRelativeTime(c.last_synced_at)}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-1 text-xs font-medium text-slate-600 dark:text-slate-300 pt-3 border-t border-slate-100 dark:border-slate-800">
        Ver análise <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </button>
  );
}

// ============================================================================
// Linha desktop
// ============================================================================

function CampaignRowDesktop({ campaign: c, onOpen }) {
  const resultado = (c.faturamento_total_30d || 0) - (c.custo_total_30d || 0);

  return (
    <tr
      onClick={onOpen}
      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
    >
      <td className="px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
            {c.campaign_name || "—"}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-mono">
            ID: {c.external_id}
          </p>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={c.campaign_status} />
      </td>
      <td className="px-4 py-3 text-right text-sm text-slate-900 dark:text-slate-100 tabular-nums">
        {formatBRL(c.custo_total_30d)}
      </td>
      <td className="px-4 py-3 text-right text-sm text-slate-900 dark:text-slate-100 tabular-nums">
        {formatBRL(c.faturamento_total_30d)}
      </td>
      <td
        className={`px-4 py-3 text-right text-sm font-semibold tabular-nums ${
          resultado > 0
            ? "text-emerald-600 dark:text-emerald-400"
            : resultado < 0
            ? "text-red-600 dark:text-red-400"
            : "text-slate-900 dark:text-slate-100"
        }`}
      >
        {formatBRL(resultado)}
      </td>
      <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
        {formatRelativeTime(c.last_synced_at)}
      </td>
    </tr>
  );
}

// ============================================================================
// Status com ícone (segue design system)
// ============================================================================

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();

  if (s === "ENABLED") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Ativa
      </span>
    );
  }

  if (s === "PAUSED") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
        <CircleDashed className="w-3.5 h-3.5" />
        Pausada
      </span>
    );
  }

  if (s === "REMOVED") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
        <AlertCircle className="w-3.5 h-3.5" />
        Removida
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
      <CircleDashed className="w-3.5 h-3.5" />
      {status || "—"}
    </span>
  );
}